import { HashRouter, Routes, Route, useLocation } from 'react-router-dom';
import React, { Suspense, useEffect } from 'react';
import { useInteractionCapture } from './hooks/useInteractionCapture';
import { Toaster, toast } from 'react-hot-toast';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import ProfileReminder from './components/common/ProfileReminder';
import Dashboard from './pages/Dashboard';
import Login from './pages/Login';
import Register from './pages/Register';
import Projects from './pages/Projects';
import ProjectWorkspace from './pages/workspace/ProjectWorkspace';
import VirtualWorkspace from './pages/VirtualWorkspace';
import ClientPortal from './pages/ClientPortal';
import Skills from './pages/Skills';
import MFA from './pages/MFA';
import Dispatch from './pages/Dispatch';
import ApplicationsDashboard from './pages/ApplicationsDashboard';
import ProfileCompletion from './pages/ProfileCompletion';
import SkillPassport from './pages/talent/SkillPassport';
import { AuthProvider } from './context/AuthContext';
import LandingPage from './pages/LandingPage';
import Settings from './pages/Settings';
import DepositCallback from './pages/Financial/DepositCallback';
import FinancialDashboard from './pages/Financial/Dashboard';
import AdminFinancialDashboard from './pages/Admin/AdminFinancialDashboard';
import AboutUs from './pages/AboutUs';
import ContactUs from './pages/ContactUs';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import Support from './pages/Support';
import LaunchPost from './pages/LaunchPost';
import IncomeDashboard from './pages/Financial/IncomeDashboard';
import Leaderboard from './pages/Leaderboard';

// Lazily-load heavier/less-critical pages to reduce initial bundle size
const Academy = React.lazy(() => import('./pages/Academy'));
const HRPayroll = React.lazy(() => import('./pages/HRPayroll'));
const TalentOverview = React.lazy(() => import('./pages/TalentOverview'));
const ThreeDHub = React.lazy(() => import('./pages/ThreeDHub'));
const UnityBot = React.lazy(() => import('./pages/UnityBot'));
const Notifications = React.lazy(() => import('./pages/Notifications'));

let startupTime = '';

function AutoUpdater() {
  useEffect(() => {
    const checkVersion = async () => {
      try {
        const userServiceURL = import.meta.env.VITE_USER_SERVICE_URL || 'http://localhost:4000';
        // Avoid double /api if service URL already includes it
        const endpoint = userServiceURL.endsWith('/api') ? '/health' : '/api/health';
        const res = await fetch(`${userServiceURL}${endpoint}`, { cache: 'no-store' });
        const data = await res.json();

        if (startupTime && data.startupTime && data.startupTime !== startupTime) {
          console.log('🔄 New platform deployment detected, auto-updating...');
          toast.success('Platform update detected! Applying changes...', {
            duration: 5000,
            icon: '✨',
          });

          setTimeout(() => {
            window.location.reload();
          }, 2000);
        }

        if (!startupTime && data.startupTime) {
          startupTime = data.startupTime;
        }
      } catch (err) {
        // Silently fail if health check fails
      }
    };

    // Check every 2 minutes
    checkVersion();
    const interval = setInterval(checkVersion, 120000);
    return () => clearInterval(interval);
  }, []);
  return null;
}

function AppContent() {
  const location = useLocation();
  const isLanding = location.pathname === '/';
  
  // Initialize Global Interaction Capture
  useInteractionCapture();

  // Landing page has its own layout
  if (isLanding) {
    return <LandingPage />;
  }

  // All other pages render directly without any wrapper
  return (
    <Suspense
      fallback={
        <div className="min-h-screen bg-premium-wood-dark flex items-center justify-center">
          <div className="text-center text-white">
            <div className="animate-spin w-12 h-12 border-4 border-premium-gold border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-white/80">Loading...</p>
          </div>
        </div>
      }
    >
      <AutoUpdater />
      <Routes>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/mfa" element={<MFA />} />
        <Route path="/profile-completion" element={<ProfileCompletion />} />
        <Route path="/profile-completion" element={<ProfileCompletion />} />
        <Route path="/dispatch" element={<Dispatch />} />
        <Route path="/dispatch/tasks/:taskId" element={<Dispatch />} />
        <Route path="/dispatch/applications" element={<ApplicationsDashboard />} />
        <Route path="/notifications" element={<Notifications />} />
        <Route path="/projects" element={<Projects />} />
        <Route path="/projects/:projectId" element={<ProjectWorkspace />} />
        <Route path="/skills" element={<Skills />} />
        <Route path="/workspace" element={<VirtualWorkspace />} />
        <Route path="/client" element={<ClientPortal />} />
        <Route path="/academy" element={<Academy />} />
        <Route path="/hr" element={<HRPayroll />} />
        <Route path="/talent" element={<TalentOverview />} />
        <Route path="/talent/passport/:userId" element={<SkillPassport />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/3dhub" element={<ThreeDHub />} />
        <Route path="/unitybot" element={<UnityBot />} />
        <Route path="/financial" element={<FinancialDashboard />} />
        <Route path="/financial/income" element={<IncomeDashboard />} />
        <Route path="/financial/deposit/callback" element={<DepositCallback />} />
        <Route path="/admin/finance" element={<AdminFinancialDashboard />} />
        <Route path="/about" element={<AboutUs />} />
        <Route path="/contact" element={<ContactUs />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/support" element={<Support />} />
        <Route path="/launch" element={<LaunchPost />} />
        <Route path="/leaderboard" element={<Leaderboard />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  const recaptchaSiteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY || '';

  return (
    <GoogleReCaptchaProvider reCaptchaKey={recaptchaSiteKey}>
      <AuthProvider>
        <HashRouter>
          <AppContent />
          <ProfileReminder />
          <Toaster position="top-right" reverseOrder={false} />
        </HashRouter>
      </AuthProvider>
    </GoogleReCaptchaProvider>
  );
}

export default App;